import classNames from "classnames";
import { CardVariant, MDOption, MetadataType } from "types";
import MetadataOptions from "../MetadataOptions";
import ChevronIcon from "assets/icons/ChevronIcon";
import { useEffect, useRef, useState } from "react";
import LabelRowWithOverflowCount from "./LabelsWithOverflow";
import { formatUnderscoreWithSpace } from "helpers/utility/formatters";
import styles from "./style.module.scss";
import { CardHeaderProps } from "../../pages/DataPage/CardsView/DataList/EnrichmentCard/CardHeader";
import { CardHeaderSuffix } from "../../pages/DataPage/CardsView/DataList/EnrichmentCard/CardHeader/HeaderTitle";

export interface CardMetadataLabelsProps {
  labels: string[];
  options?: MDOption[];
  metadataType: MetadataType;
  cardVariant: CardVariant;
  labelIcon: JSX.Element;
  labelType?: string;
  showOptionsIcon: boolean;
  selectedLabels?: string[];
  onLabelSelection?: (label: string) => void;
  onViewClick?: (type: MetadataType) => void;
  onViewAllClick?: (type: MetadataType) => void;
  suffix?: CardHeaderProps["suffix"];
  interactive?: boolean;
}

const CardMetadataLabels = ({
  labels,
  options = [],
  metadataType,
  cardVariant,
  labelIcon,
  selectedLabels = [],
  showOptionsIcon,
  onViewClick,
  onViewAllClick,
  onLabelSelection,
  suffix,
  labelType,
  interactive = true,
}: CardMetadataLabelsProps) => {
  const [showOptions, setShowOptions] = useState(false);
  const filterBtnRef = useRef<HTMLDivElement>(null);

  const name =
    metadataType === MetadataType.OBJECT_LABELS
      ? "Objects"
      : formatUnderscoreWithSpace(
          metadataType.toLowerCase().replace("image", "").replace("object", ""),
        );
  const noLabelsText = `No ${name} available`;

  const toggleShowOptions = () => {
    if (showOptionsIcon) {
      setShowOptions(!showOptions);
    }
  };

  const handleViewClick = (type: MetadataType) => {
    onViewClick?.(type);
    toggleShowOptions();
  };

  const handleViewAllClick = (type: MetadataType) => {
    onViewAllClick?.(type);
    toggleShowOptions();
  };

  const handleOutsideClick = (e: any) => {
    if (filterBtnRef.current && !filterBtnRef.current.contains(e.target)) {
      toggleShowOptions();
    }
  };

  useEffect(() => {
    if (showOptions) {
      document.addEventListener("click", handleOutsideClick);
    } else {
      document.removeEventListener("click", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, [showOptions]);

  const filterClassnames = classNames(
    styles.metadataFilter,
    styles.noDetailsClick,
    {
      [styles.interactive]: interactive,
      [styles.objectCaptions]: metadataType === MetadataType.OBJECT_CAPTIONS,
      [styles.imageCaptions]: metadataType === MetadataType.IMAGE_CAPTIONS,
      [styles.objectLabels]: metadataType === MetadataType.OBJECT_LABELS,
      [styles.imageLabels]: metadataType === MetadataType.IMAGE_LABELS,
      [styles.userTags]: metadataType === MetadataType.USER_TAGS,
      [styles.similarityMetadataFilter]: cardVariant === CardVariant.VERTEX,
      [styles.exportMetadataFilter]: cardVariant === CardVariant.SELECTED,
      [styles.onlyIcon]: !showOptionsIcon,
      [styles.unsetWidth]: Boolean(labelType),
    },
  );

  const noLabelsClassname = classNames(styles.noLabelsText, {
    [styles.similarityText]: cardVariant === CardVariant.VERTEX,
    [styles.exportText]: cardVariant === CardVariant.SELECTED,
  });

  const renderButton = () => (
    <div
      ref={filterBtnRef}
      onClick={toggleShowOptions}
      className={filterClassnames}
      data-testid="metadata-filter"
    >
      <div className={styles.labelIcon}>
        {labelIcon}
        {labelType}
      </div>
      {showOptionsIcon && !labelType && (
        <div
          className={styles.dropdownIcon}
          data-testid="md-filter-chevron-icon"
        >
          {showOptions ? (
            <ChevronIcon direction="up" dataTestId="chevron-up-icon" />
          ) : (
            <ChevronIcon direction="down" dataTestId="chevron-down-icon" />
          )}
        </div>
      )}
    </div>
  );

  const emptyMessage = (
    <span className={noLabelsClassname}>{noLabelsText}</span>
  );

  return (
    <div
      className={classNames(styles.metadataContainer, {
        [styles.withSuffix]: Boolean(suffix),
      })}
    >
      {renderButton()}

      <div
        className={classNames(styles.labelsSection, {
          [styles.labelsSectionWithoutDropdown]: !showOptionsIcon,
        })}
      >
        {labels.length ? (
          <LabelRowWithOverflowCount
            labels={labels}
            cardVariant={cardVariant}
            selectedLabels={selectedLabels}
            onLabelSelection={onLabelSelection}
          />
        ) : (
          emptyMessage
        )}
      </div>
      {suffix && <CardHeaderSuffix {...suffix} />}
      {showOptions && (
        <MetadataOptions
          options={options}
          onViewClick={handleViewClick}
          onViewAllClick={handleViewAllClick}
        />
      )}
    </div>
  );
};

export default CardMetadataLabels;
